/**
 * stripPlaceholderShlokas — EEAT protection (Feb 07, 2026 P0 fix)
 *
 * Legacy blogs (published before the Feb-07 fact-validator upgrade) may still
 * contain placeholder shloka blocks like:
 *   • <em>इस विषय पर प्रमाणित शास्त्रीय संदर्भ खोज में है। जल्द ही यहाँ जोड़ा जाएगा।</em>
 *   • <p>श्लोक उपलब्ध नहीं है ...</p>
 *   • <p><strong>Shloka:</strong> - </p> with Source/Meaning/Interpretation = "-"
 *   • <p>Coming Soon</p>, <p>TODO</p>, <p>Placeholder</p>, etc.
 *
 * These damage EEAT, credibility, and shastriya authority. This enhancer
 * removes them BEFORE the blog HTML is rendered to the user — no DB
 * migration needed. Runs after shloka enhancer so it catches enriched
 * labelled blocks too.
 */

// User-mandated forbidden phrase list (must stay in sync with backend
// _PLACEHOLDER_FORBIDDEN_PHRASES in blog_fact_validator.py).
const PLACEHOLDER_RE = new RegExp(
  '(?:' + [
    // Hindi shloka placeholders
    'श्लोक\\s*उपलब्ध\\s*नहीं',
    'ग्रंथों\\s+में\\s+श्लोक\\s+उपलब्ध\\s+नहीं',
    'प्रमाणित\\s+शास्त्रीय\\s+संदर्भ\\s+खोज\\s+में\\s+है',
    'जल्द\\s*ही\\s*जोड़ा\\s*जाएगा',
    'जल्दी\\s*जोड़ा\\s*जाएगा',
    'जल्द\\s*ही\\s*यहाँ\\s*जोड़ा\\s*जाएगा',
    'संदर्भ\\s*उपलब्ध\\s*नहीं',
    'जल्द\\s*ही\\s*उपलब्ध',
    // English placeholders
    'coming\\s*soon',
    'to\\s+be\\s+added',
    'placeholder(?:\\s*text)?',
    'under\\s*review',
    'lorem\\s+ipsum',
    'TODO',
  ].join('|') + ')',
  'i',
);

// Dash-only source/meaning/interpretation labels — either loose ("-") or
// with surrounding whitespace only.
const EMPTY_LABEL_RE =
  /<(p|div)[^>]*>\s*<(strong|b)[^>]*>\s*(Source|Meaning|Interpretation)\s*:\s*<\/\2>\s*[-–—\s]*\s*<\/\1>/gi;

/**
 * Test whether the plain-text content of an HTML fragment contains any
 * placeholder phrase.
 */
function _isPlaceholder(fragment) {
  if (!fragment) return false;
  const plain = String(fragment).replace(/<[^>]+>/g, ' ').trim();
  if (!plain) return false;
  return PLACEHOLDER_RE.test(plain);
}

/**
 * Remove any HTML element (with matching open/close tag) whose text content
 * consists ONLY of a placeholder phrase. Handles <p>, <em>, <blockquote>,
 * <div>, <li>. Non-greedy per-element so nested unrelated content is safe.
 */
function _stripPlaceholderElements(html) {
  const tags = ['p', 'em', 'blockquote', 'div', 'li', 'figcaption'];
  let out = html;
  for (const tag of tags) {
    const re = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi');
    out = out.replace(re, (match, inner) => (_isPlaceholder(inner) ? '' : match));
  }
  return out;
}

/**
 * Remove entire labelled shloka `.shloka.labelled` cards whose body is all
 * dashes or empty. Matches the DOM output of enhanceLabelledShlokas().
 */
function _stripEmptyLabelledShlokaCards(html) {
  // Card pattern: <div class="shloka labelled">…</div>
  return html.replace(
    /<div\s+[^>]*class=["'][^"']*\bshloka\b[^"']*\blabelled\b[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi,
    (match, inner) => {
      // Strip HTML → normalize → count meaningful chars
      const plain = inner.replace(/<[^>]+>/g, ' ').replace(/[\s\-–—]+/g, '').trim();
      // If entire card body reduces to nothing after stripping dashes/whitespace,
      // OR if the card contains a placeholder phrase, drop it.
      if (!plain || _isPlaceholder(inner)) return '';
      return match;
    },
  );
}

/**
 * Remove H2 shloka section headings whose subsequent block-level content is
 * entirely stripped by the passes above — an empty section heading is worse
 * than no section at all.
 */
function _stripOrphanShlokaHeadings(html) {
  // Find <h2>…शास्त्रीय संदर्भ / शास्त्रीय आधार / Shloka Reference…</h2>
  // followed by content up to the next <h1>/<h2>/end.
  const re = /<h2\b[^>]*>[^<]*(?:शास्त्रीय\s*संदर्भ|शास्त्रीय\s*आधार|Shloka\s*Reference)[^<]*<\/h2>\s*([\s\S]*?)(?=<h[12]\b|<\/article>|$)/gi;
  return html.replace(re, (match, body) => {
    // Strip inline whitespace + tags from body — if nothing left, drop the whole section
    const plain = body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    return plain ? match : '';
  });
}

/**
 * Public entry point — apply all three passes.
 */
export function stripPlaceholderShlokas(html) {
  if (!html || typeof html !== 'string') return html || '';
  let out = html;
  // 1. Remove empty Source:-/Meaning:-/Interpretation:- lines
  out = out.replace(EMPTY_LABEL_RE, '');
  // 2. Drop empty labelled shloka cards
  out = _stripEmptyLabelledShlokaCards(out);
  // 3. Drop any placeholder-only element
  out = _stripPlaceholderElements(out);
  // 4. Drop orphan section headings that lost all their content
  out = _stripOrphanShlokaHeadings(out);
  // 5. Collapse any 3+ consecutive blank lines that resulted
  out = out.replace(/(\s*\n){3,}/g, '\n\n');
  return out;
}

export default stripPlaceholderShlokas;
